import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { monitoringAPI } from '../../services/api';
import { toast } from 'react-hot-toast';
import DashboardLayout from '../../components/layout/DashboardLayout';
import useDocumentTitle from '../../hooks/useDocumentTitle';
import PerformanceInsights from './PerformanceInsights';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  Legend,
  ComposedChart,
  Line,
} from 'recharts';

interface PerformanceMetric {
  date: string;
  avg_response_time: number;
  max_response_time: number;
  min_response_time: number;
  downtime_minutes: number;
  total_checks: number;
  failed_checks: number;
}

const PerformanceReportPage: React.FC = () => {
  useDocumentTitle('Performance Report');
  const { currentAsset } = useAuth();
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState(30);
  const [metrics, setMetrics] = useState<PerformanceMetric[]>([]);
  const [summary, setSummary] = useState({
    avg_response_time: 0,
    total_downtime: 0,
    availability: 100,
    total_checks: 0,
  });

  useEffect(() => {
    if (currentAsset) {
      loadPerformanceData();
    }
  }, [currentAsset, period]);

  const loadPerformanceData = async () => {
    if (!currentAsset) return;

    setLoading(true);
    try {
      const response = await monitoringAPI.getPerformance(currentAsset.id, period);
      if (response.data.status === 'success') {
        const data = response.data.data;
        setMetrics(data.daily_metrics || []);
        setSummary({
          avg_response_time: data.avg_response_time || 0,
          total_downtime: data.total_downtime || 0,
          availability: data.availability ?? 100,
          total_checks: data.total_checks || 0,
        });
      }
    } catch (error: any) {
      if (error.response?.status !== 401) {
        console.error('Failed to load performance data:', error);
        toast.error('Failed to load performance data');
      }
    } finally {
      setLoading(false);
    }
  };

  const formatDuration = (minutes: number) => {
    if (minutes === 0) return '0m';
    if (minutes < 60) return `${Math.round(minutes)}m`;
    const hours = Math.floor(minutes / 60);
    const mins = Math.round(minutes % 60);
    return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
  };

  const getAvailabilityColor = () => {
    if (summary.availability >= 99) return 'text-success';
    if (summary.availability >= 95) return 'text-warning';
    return 'text-danger';
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const chartData = metrics.map(m => ({
    ...m,
    date: formatDate(m.date),
    fullDate: m.date,
    uptime: m.total_checks > 0 ? ((m.total_checks - m.failed_checks) / m.total_checks * 100).toFixed(1) : 100,
  }));

  if (loading) {
    return (
      <DashboardLayout>
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
          <div className="text-center">
            <div className="spinner-border text-primary mb-3" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="text-muted">Loading performance data...</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="container-fluid">
        {/* Header */}
        <div className="d-flex justify-content-between align-items-center mb-4">
          <div>
            <h1 className="h3 mb-1">Performance Report</h1>
            <p className="text-muted mb-0">Detailed performance metrics and downtime analysis</p>
          </div>
          <div className="d-flex gap-2">
            <div className="btn-group">
              <button
                className={`btn btn-sm ${period === 7 ? 'btn-primary' : 'btn-outline-secondary'}`}
                onClick={() => setPeriod(7)}
              >
                7 Days
              </button>
              <button
                className={`btn btn-sm ${period === 30 ? 'btn-primary' : 'btn-outline-secondary'}`}
                onClick={() => setPeriod(30)}
              >
                30 Days
              </button>
              <button
                className={`btn btn-sm ${period === 90 ? 'btn-primary' : 'btn-outline-secondary'}`}
                onClick={() => setPeriod(90)}
              >
                90 Days
              </button>
            </div>
            <button className="btn btn-sm btn-outline-primary" onClick={loadPerformanceData}>
              <i className="bi bi-arrow-clockwise me-1"></i>Refresh
            </button>
          </div>
        </div>

        {currentAsset && <PerformanceInsights assetId={currentAsset.id} days={period} />}

        {/* Summary Cards */}
        <div className="row mb-4 g-3">
          <div className="col-md-3 col-sm-6">
            <div className="card border-0 shadow-sm h-100">
              <div className="card-body">
                <div className="d-flex align-items-center">
                  <div className="flex-shrink-0">
                    <div className="bg-primary bg-opacity-10 rounded-3 p-3">
                      <i className="bi bi-speedometer2 text-primary fs-4"></i>
                    </div>
                  </div>
                  <div className="flex-grow-1 ms-3">
                    <p className="text-muted mb-1 small">Avg Response Time</p>
                    <h4 className="mb-0 fw-bold">{summary.avg_response_time.toFixed(0)}ms</h4>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="col-md-3 col-sm-6">
            <div className="card border-0 shadow-sm h-100">
              <div className="card-body">
                <div className="d-flex align-items-center">
                  <div className="flex-shrink-0">
                    <div className={`bg-${summary.total_downtime > 0 ? 'danger' : 'success'} bg-opacity-10 rounded-3 p-3`}>
                      <i className={`bi bi-clock-history ${summary.total_downtime > 0 ? 'text-danger' : 'text-success'} fs-4`}></i>
                    </div>
                  </div>
                  <div className="flex-grow-1 ms-3">
                    <p className="text-muted mb-1 small">Total Downtime</p>
                    <h4 className={`mb-0 fw-bold ${summary.total_downtime > 0 ? 'text-danger' : 'text-success'}`}>
                      {formatDuration(summary.total_downtime)}
                    </h4>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="col-md-3 col-sm-6">
            <div className="card border-0 shadow-sm h-100">
              <div className="card-body">
                <div className="d-flex align-items-center">
                  <div className="flex-shrink-0">
                    <div className={`bg-${summary.availability >= 99 ? 'success' : summary.availability >= 95 ? 'warning' : 'danger'} bg-opacity-10 rounded-3 p-3`}>
                      <i className={`bi bi-shield-check ${getAvailabilityColor()} fs-4`}></i>
                    </div>
                  </div>
                  <div className="flex-grow-1 ms-3">
                    <p className="text-muted mb-1 small">Availability</p>
                    <h4 className={`mb-0 fw-bold ${getAvailabilityColor()}`}>{summary.availability.toFixed(2)}%</h4>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="col-md-3 col-sm-6">
            <div className="card border-0 shadow-sm h-100">
              <div className="card-body">
                <div className="d-flex align-items-center">
                  <div className="flex-shrink-0">
                    <div className="bg-info bg-opacity-10 rounded-3 p-3">
                      <i className="bi bi-check2-all text-info fs-4"></i>
                    </div>
                  </div>
                  <div className="flex-grow-1 ms-3">
                    <p className="text-muted mb-1 small">Total Checks</p>
                    <h4 className="mb-0 fw-bold text-info">{summary.total_checks.toLocaleString()}</h4>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {metrics.length === 0 ? (
          <div className="card border-0 shadow-sm">
            <div className="card-body text-center py-5">
              <div className="mb-4">
                <div className="bg-light rounded-circle d-inline-flex align-items-center justify-content-center" style={{ width: '80px', height: '80px' }}>
                  <i className="bi bi-bar-chart-line text-muted fs-1"></i>
                </div>
              </div>
              <h5 className="text-muted mb-2">No Performance Data Yet</h5>
              <p className="text-muted mb-4">
                Performance metrics will appear here once health checks start running.<br />
                Make sure you have URLs configured for monitoring.
              </p>
              <button className="btn btn-primary" onClick={loadPerformanceData}>
                <i className="bi bi-arrow-clockwise me-2"></i>Check Again
              </button>
            </div>
          </div>
        ) : (
          <>
            {/* Response Time Chart */}
            <div className="row mb-4">
              <div className="col-12">
                <div className="card border-0 shadow-sm">
                  <div className="card-header bg-white border-0 py-3">
                    <div className="d-flex justify-content-between align-items-center">
                      <div>
                        <h5 className="mb-1">Response Time Trend</h5>
                        <small className="text-muted">Average, minimum and maximum response times</small>
                      </div>
                      <div className="d-flex gap-3">
                        <span className="d-flex align-items-center gap-2 small">
                          <span className="rounded-circle" style={{ width: '10px', height: '10px', backgroundColor: '#10b981' }}></span>
                          Min
                        </span>
                        <span className="d-flex align-items-center gap-2 small">
                          <span className="rounded-circle" style={{ width: '10px', height: '10px', backgroundColor: '#3b82f6' }}></span>
                          Avg
                        </span>
                        <span className="d-flex align-items-center gap-2 small">
                          <span className="rounded-circle" style={{ width: '10px', height: '10px', backgroundColor: '#f59e0b' }}></span>
                          Max
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="card-body">
                    <div style={{ height: '320px' }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <ComposedChart data={chartData} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
                          <defs>
                            <linearGradient id="colorAvg" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3}/>
                              <stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/>
                            </linearGradient>
                          </defs>
                          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                          <XAxis
                            dataKey="date"
                            tick={{ fill: '#6b7280', fontSize: 12 }}
                            tickLine={false}
                            axisLine={{ stroke: '#e5e7eb' }}
                          />
                          <YAxis
                            tick={{ fill: '#6b7280', fontSize: 12 }}
                            tickLine={false}
                            axisLine={{ stroke: '#e5e7eb' }}
                            tickFormatter={(value) => `${value}ms`}
                          />
                          <Tooltip
                            contentStyle={{
                              backgroundColor: '#fff',
                              border: '1px solid #e5e7eb',
                              borderRadius: '8px',
                              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                            }}
                            formatter={(value) => [`${Number(value).toFixed(0)}ms`]}
                            labelFormatter={(label) => `Date: ${label}`}
                          />
                          <Area
                            type="monotone"
                            dataKey="avg_response_time"
                            stroke="#3b82f6"
                            fillOpacity={1}
                            fill="url(#colorAvg)"
                            strokeWidth={2}
                            name="Avg"
                          />
                          <Line
                            type="monotone"
                            dataKey="min_response_time"
                            stroke="#10b981"
                            strokeWidth={2}
                            dot={false}
                            name="Min"
                          />
                          <Line
                            type="monotone"
                            dataKey="max_response_time"
                            stroke="#f59e0b"
                            strokeWidth={2}
                            dot={false}
                            name="Max"
                          />
                        </ComposedChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Uptime & Failures Chart */}
            <div className="row mb-4">
              <div className="col-md-8">
                <div className="card border-0 shadow-sm h-100">
                  <div className="card-header bg-white border-0 py-3">
                    <h5 className="mb-1">Health Checks Overview</h5>
                    <small className="text-muted">Successful vs failed checks per day</small>
                  </div>
                  <div className="card-body">
                    <div style={{ height: '280px' }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={chartData} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
                          <XAxis
                            dataKey="date"
                            tick={{ fill: '#6b7280', fontSize: 12 }}
                            tickLine={false}
                            axisLine={{ stroke: '#e5e7eb' }}
                          />
                          <YAxis
                            tick={{ fill: '#6b7280', fontSize: 12 }}
                            tickLine={false}
                            axisLine={{ stroke: '#e5e7eb' }}
                          />
                          <Tooltip
                            contentStyle={{
                              backgroundColor: '#fff',
                              border: '1px solid #e5e7eb',
                              borderRadius: '8px',
                              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                            }}
                          />
                          <Legend />
                          <Bar
                            dataKey="total_checks"
                            name="Total Checks"
                            fill="#3b82f6"
                            radius={[4, 4, 0, 0]}
                            barSize={20}
                          />
                          <Bar
                            dataKey="failed_checks"
                            name="Failed Checks"
                            fill="#ef4444"
                            radius={[4, 4, 0, 0]}
                            barSize={20}
                          />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-md-4">
                <div className="card border-0 shadow-sm h-100">
                  <div className="card-header bg-white border-0 py-3">
                    <h5 className="mb-1">Downtime by Day</h5>
                    <small className="text-muted">Minutes of downtime per day</small>
                  </div>
                  <div className="card-body">
                    <div style={{ height: '280px' }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={chartData} layout="vertical" margin={{ top: 10, right: 30, left: 50, bottom: 0 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" horizontal={false} />
                          <XAxis
                            type="number"
                            tick={{ fill: '#6b7280', fontSize: 12 }}
                            tickLine={false}
                            axisLine={{ stroke: '#e5e7eb' }}
                            tickFormatter={(value) => `${value}m`}
                          />
                          <YAxis
                            dataKey="date"
                            type="category"
                            tick={{ fill: '#6b7280', fontSize: 11 }}
                            tickLine={false}
                            axisLine={{ stroke: '#e5e7eb' }}
                            width={50}
                          />
                          <Tooltip
                            contentStyle={{
                              backgroundColor: '#fff',
                              border: '1px solid #e5e7eb',
                              borderRadius: '8px',
                              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                            }}
                            formatter={(value) => [`${value} minutes`, 'Downtime']}
                          />
                          <Bar
                            dataKey="downtime_minutes"
                            fill="#ef4444"
                            radius={[0, 4, 4, 0]}
                            barSize={12}
                          />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Detailed Metrics Table */}
            <div className="row">
              <div className="col-12">
                <div className="card border-0 shadow-sm">
                  <div className="card-header bg-white border-0 py-3">
                    <h5 className="mb-0">Daily Performance Metrics</h5>
                  </div>
                  <div className="card-body p-0">
                    <div className="table-responsive">
                      <table className="table table-hover align-middle mb-0">
                        <thead>
                          <tr className="bg-light">
                            <th className="border-0 ps-4">Date</th>
                            <th className="border-0 text-center">Avg Response</th>
                            <th className="border-0 text-center">Min / Max</th>
                            <th className="border-0 text-center">Uptime</th>
                            <th className="border-0 text-center">Checks</th>
                            <th className="border-0 text-center pe-4">Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {metrics.slice().reverse().map((metric, index) => {
                            const uptime = metric.total_checks > 0
                              ? ((metric.total_checks - metric.failed_checks) / metric.total_checks * 100)
                              : 100;
                            return (
                              <tr key={index}>
                                <td className="ps-4">
                                  <span className="fw-medium">{new Date(metric.date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}</span>
                                </td>
                                <td className="text-center">
                                  <span className={`fw-semibold ${metric.avg_response_time > 500 ? 'text-danger' : metric.avg_response_time > 300 ? 'text-warning' : 'text-success'}`}>
                                    {metric.avg_response_time.toFixed(0)}ms
                                  </span>
                                </td>
                                <td className="text-center">
                                  <span className="text-success">{metric.min_response_time.toFixed(0)}ms</span>
                                  <span className="text-muted mx-1">/</span>
                                  <span className="text-warning">{metric.max_response_time.toFixed(0)}ms</span>
                                </td>
                                <td className="text-center">
                                  <div className="d-flex align-items-center justify-content-center gap-2">
                                    <div className="progress" style={{ width: '60px', height: '6px' }}>
                                      <div
                                        className={`progress-bar bg-${uptime >= 99 ? 'success' : uptime >= 95 ? 'warning' : 'danger'}`}
                                        style={{ width: `${uptime}%` }}
                                      ></div>
                                    </div>
                                    <span className={`small fw-medium ${uptime >= 99 ? 'text-success' : uptime >= 95 ? 'text-warning' : 'text-danger'}`}>
                                      {uptime.toFixed(1)}%
                                    </span>
                                  </div>
                                </td>
                                <td className="text-center">
                                  <span className="text-muted">{metric.total_checks - metric.failed_checks}</span>
                                  <span className="text-muted mx-1">/</span>
                                  <span className="text-muted">{metric.total_checks}</span>
                                  {metric.failed_checks > 0 && (
                                    <span className="badge bg-danger ms-2">{metric.failed_checks} failed</span>
                                  )}
                                </td>
                                <td className="text-center pe-4">
                                  {metric.failed_checks === 0 ? (
                                    <span className="badge bg-success-subtle text-success">
                                      <i className="bi bi-check-circle me-1"></i>Healthy
                                    </span>
                                  ) : metric.failed_checks < metric.total_checks ? (
                                    <span className="badge bg-warning-subtle text-warning">
                                      <i className="bi bi-exclamation-triangle me-1"></i>Degraded
                                    </span>
                                  ) : (
                                    <span className="badge bg-danger-subtle text-danger">
                                      <i className="bi bi-x-circle me-1"></i>Down
                                    </span>
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </DashboardLayout>
  );
};

export default PerformanceReportPage;
